// Customer create/edit form page — /customers/new and /customers/:id/edit
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Customer {
  id: string; phone: string; name: string; email: string;
  address: string; birthday?: string; gender?: string; note: string;
  customerType?: string;
}

interface FormState {
  phone: string; name: string; email: string;
  address: string; birthday: string; gender: string; note: string;
  customerType: string;
}

const empty: FormState = { phone: "", name: "", email: "", address: "", birthday: "", gender: "", note: "", customerType: "retail" };

export function CustomerFormPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const isEdit = !!params.id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState("");

  const { data: c } = useQuery({
    queryKey: ["customer", params.id],
    queryFn: () => apiGet<Customer>(`/v1/customers/${params.id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (c) {
      setForm({
        phone: c.phone, name: c.name, email: c.email,
        address: c.address, birthday: c.birthday ?? "", gender: c.gender ?? "", note: c.note,
        customerType: c.customerType ?? "retail",
      });
    }
  }, [c]);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const createMut = useMutation({
    mutationFn: (body: FormState) => apiPost<Customer>("/v1/customers", body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      navigate({ to: `/customers/${res.id}` as any });
    },
    onError: (err: unknown) => setError(translateApiError(err)),
  });

  const updateMut = useMutation({
    mutationFn: (body: Partial<FormState>) => apiPatch<Customer>(`/v1/customers/${params.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", params.id] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      navigate({ to: `/customers/${params.id}` as any });
    },
    onError: (err: unknown) => setError(translateApiError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (isEdit) {
      const { phone: _p, ...patch } = form;
      updateMut.mutate(patch);
    } else {
      createMut.mutate(form);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const backTo: any = isEdit ? `/customers/${params.id}` : "/customers";

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="max-w-lg space-y-4">
      <div>
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Quay lại
        </Link>
        <h1 className="text-2xl font-bold mt-1">{isEdit ? "Chỉnh sửa khách hàng" : "Thêm khách hàng"}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-card p-6">
        {error && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

        <div className="space-y-1">
          <label className="text-sm font-medium">Số điện thoại *</label>
          <Input value={form.phone} onChange={set("phone")} placeholder="0912345678" disabled={isEdit} required />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Tên khách hàng</label>
          <Input value={form.name} onChange={set("name")} placeholder="Nguyễn Văn A" />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Email</label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="email@example.com" />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Địa chỉ</label>
          <Input value={form.address} onChange={set("address")} placeholder="123 Đường ABC, Quận 1" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Ngày sinh</label>
            <Input type="date" value={form.birthday} onChange={set("birthday")} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Giới tính</label>
            <select
              value={form.gender}
              onChange={set("gender")}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">—</option>
              <option value="male">Nam</option>
              <option value="female">Nữ</option>
              <option value="other">Khác</option>
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Loại khách hàng</label>
          <select
            value={form.customerType}
            onChange={set("customerType")}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="retail">Lẻ</option>
            <option value="wholesale">Sỉ</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Ghi chú</label>
          <textarea
            value={form.note}
            onChange={set("note")}
            rows={3}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
            placeholder="Ghi chú về khách hàng..."
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Link to={backTo} className="flex-1">
            <Button variant="outline" type="button" className="w-full">Hủy</Button>
          </Link>
          <Button type="submit" disabled={isPending} className="flex-1">
            {isPending ? "Đang lưu..." : isEdit ? "Cập nhật" : "Tạo khách hàng"}
          </Button>
        </div>
      </form>
    </div>
  );
}
